import UserStatusChip from '@/components/map/UserStatusChip';
import LightTooltip from '@/components/utils/LightTooltip';
import { useEmergencyChatProvider } from '@/context_providers/emergency_chat_context/EmergencyChatProvider';
import { OrgUser } from '@/models/User/OrgUser';
import { IssueIcon } from '@/resources/icons/IssueIcon';
import { StringKeys } from '@/resources/strings/StringKeys';
import {
  useEmergencyActions,
  useEmergencyData,
} from '@/stores/map/emergencyStore';
import { getColorForGroup } from '@/utils/colorUtils';
import { Person, Phone } from '@mui/icons-material';
import { Box, ListItem, Typography } from '@mui/material';
import { t } from 'i18next';
import { useMemo } from 'react';
import { useDashboardActions } from '@/stores/map/dashboardStore';
import { UserStatusPingViewModel } from '@/models/User/UserStatusPingViewModel';
import useMapToUser from '@/hooks/useMapToUser';
import Group from '@/models/Organization/Group';
import { useUserUpdaterProvider } from '@/context_providers/user_context/UserUpdaterProvider';
import { isMobileUser } from '@/components/routing/UserRoutes/AcceptedRoles';

/**
 * @typedef {Object} LocationSharingPanelUserProps
 * @property {Group} group - The group to which the user belongs.
 * @property {OrgUser} user - The user object containing user details.
 * @property {UserStatusPingViewModel | null | undefined} userStatus - The user's location sharing status.
 * @property {Object} theme - The theme object for styling.
 */

/**
 *
 * @param {LocationSharingPanelUserProps} props
 * @returns
 */
const LocationSharingPanelUser = ({ group, user, userStatus, theme }) => {
  const dashboardActions = useDashboardActions();
  const emergencyActions = useEmergencyActions();
  const emergencyData = useEmergencyData();
  const { setSelectedEmergency } = useEmergencyChatProvider();
  const { moveMapToUser } = useMapToUser();
  const { userRole } = useUserUpdaterProvider();
  // Mobile Users must not be able to open other users' dashboards from the sidebar.
  const isCurrentUserMobileUser = isMobileUser(userRole);

  const emergency = useMemo(() => {
    const emergency = Object.values(emergencyData || {}).find(
      (element) =>
        element.emergency.isActiveEmergency() &&
        element.emergency.creator_auth_id === user.auth_id,
    );
    return emergency || null;
  }, [emergencyData, user]);

  const handleUserClick = () => {
    moveMapToUser(user.auth_id);
    // open the dashboard for the user
    dashboardActions.openDashboard(user.auth_id);
  };

  return (
    <ListItem
      key={user.auth_id}
      sx={{
        py: 0.5,
        pr: 0.5,
        pl: 1,
        backgroundColor: emergency ? '#FFD6D6' : theme.itemBackground,
        border: emergency ? '2px solid #9B0000' : 'none',
        cursor: isCurrentUserMobileUser ? 'default' : 'pointer',
        '&:hover': isCurrentUserMobileUser
          ? {}
          : emergency
            ? {
                backgroundColor: '#edcbcbff',
              }
            : {
                backgroundColor: theme.hoverBackground,
              },
        overflow: 'hidden',
        width: '100%',
      }}
      onClick={
        isCurrentUserMobileUser
          ? undefined
          : emergency
            ? () => {
                setSelectedEmergency(emergency);
                emergencyActions.setShowEmergencyChat(true);
                moveMapToUser(user.auth_id);
              }
            : () => {
                handleUserClick();
              }
      }
    >
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mb: 0.5,
            overflow: 'hidden',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 1,
              minWidth: 0,
            }}
          >
            <Person
              fontSize="small"
              sx={{
                mr: 1,
                color: getColorForGroup(group.group_name),
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'medium',
                color: theme.text,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user.first_name} {user.last_name}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {userStatus?.hasAnyDevicePermissionIssue() && (
              <LightTooltip
                title={t(StringKeys.LOCATION_PERMISSION_ISSUE_TOOLTIP)}
                placement="top"
                arrow
              >
                <IssueIcon fontSize="small" />
              </LightTooltip>
            )}

            <UserStatusChip
              hasEmergency={!!emergency}
              userStatus={userStatus}
              size="small"
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ml: 0.25,
          }}
        >
          <Phone
            fontSize="small"
            sx={{
              mr: 1,
              color: theme.mutedText,
              fontSize: '0.8rem',
            }}
          />
          <Typography variant="caption" sx={{ color: theme.mutedText }}>
            {user.phone_number}
          </Typography>
        </Box>
      </Box>
    </ListItem>
  );
};

export default LocationSharingPanelUser;
